import SEO from "@/components/SEO";
import { CheckCircle2 } from "lucide-react";

const SECTIONS = [
  {
    eyebrow: "Track",
    title: "Bulk paste. CSV drop. Instant.",
    body: "Add a handful of keywords or a thousand — in one shot. Assign country, language, device, and search engine on the fly. Import CSVs with your existing search volume data — no re-entering.",
    bullets: ["Bulk paste (one per line)", "CSV import with search volume", "CSV export ready for reports", "Tags for grouping and filters"],
  },
  {
    eyebrow: "Understand",
    title: "The Executive Summary. Every morning.",
    body: "One card summarizes what moved overnight. Improved, dropped, entered top 3, entered top 10, and estimated click impact — with the single highest-priority keyword surfaced automatically. Whether you have 5 keywords or 5,000.",
    bullets: ["Improved / dropped counts", "Entered top 3 / top 10", "Estimated click gain and loss", "Auto-picked priority keyword"],
  },
  {
    eyebrow: "Share",
    title: "White-label reports for clients.",
    body: "Send branded PDF reports and shareable links on your own domain. Your logo, your accent colour, your intro paragraph. Perfect for freelancers and agencies who don't want clients to see anyone else's brand.",
    bullets: ["One-click PDF export", "Custom share domain (reports.your-agency.com)", "Section toggles + intro copy", "Configurable sections and keyword columns"],
  },
  {
    eyebrow: "Trust",
    title: "Transparency baked in.",
    body: "We tell you exactly when data was last updated, warn you when a refresh is delayed, and never hide uncertainty. Because the data your client — or your own boss — sees needs to be the data you trust.",
    bullets: ["Last-updated timestamps everywhere", "In-progress refresh indicator", "Delayed-update warnings", "Daily update cadence"],
  },
];

export default function Features() {
  return (
    <>
      <SEO pageKey="features" title="Features — SerpNex" description="Bulk keyword add, daily Google + Bing tracking, executive summary, opportunity scoring, SEO activity timeline, white-label reports, and transparent data updates. For solo users and agencies alike." canonical="/features" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        <div className="max-w-3xl">
          <div className="eyebrow">Features</div>
          <h1 className="mt-3 h-display text-5xl sm:text-6xl font-bold text-[#0A0D14]">Everything you need. <span className="text-[#0047FF]">Nothing you don&apos;t.</span></h1>
          <p className="mt-5 text-[17px] text-[#525B6A] max-w-2xl leading-relaxed">SerpNex is deliberately narrow: keyword tracking, priority scoring, and clean client reporting. Simple enough for a blogger tracking five keywords, powerful enough for an agency managing hundreds of client projects. No backlink modules, no AI writer, no PPC tools.</p>
        </div>
      </div>

      {SECTIONS.map((s, i) => (
        <section key={i} className={`border-t border-[#E2E8F0] ${i % 2 === 1 ? 'bg-[#F8F9FA]' : ''}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-5">
              <div className="eyebrow">{s.eyebrow}</div>
              <h2 className="mt-3 text-3xl sm:text-4xl font-display font-bold text-[#0A0D14]">{s.title}</h2>
              <p className="mt-4 text-[#525B6A] leading-relaxed">{s.body}</p>
            </div>
            <div className="md:col-span-7">
              <div className="card-crisp p-6">
                <ul className="grid sm:grid-cols-2 gap-3">
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-[#0A0D14]" data-testid={`feature-bullet-${i}-${j}`}>
                      <CheckCircle2 className="h-4 w-4 text-[#0047FF] mt-0.5 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      ))}
    </>
  );
}
